import { useState, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { 
  PlusIcon, 
  TrashIcon, 
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  ListBulletIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { AppCard } from '../discovery/AppCard';
import { DroppableZone } from './DroppableZone';
import { ConnectionCanvas } from './ConnectionCanvas';
import { PlanningToolbar } from './PlanningToolbar';
import { useAppStore } from '../../stores/appStore';
import { usePlanningStore } from '../../stores/planningStore';
import type { App, ConnectionPlan } from '../../types';

interface ConnectionPlannerProps {
  onSave?: (plan: ConnectionPlan) => void;
  onLoad?: (planId: string) => void;
  readonly?: boolean;
}

// Detect if we're on a touch device
const isTouchDevice = 'ontouchstart' in window;

export function ConnectionPlanner({ onSave, onLoad, readonly = false }: ConnectionPlannerProps) {
  const { apps, isLoading } = useAppStore();
  const { 
    currentPlan, 
    addAppToPlan, 
    removeAppFromPlan, 
    addConnection, 
    removeConnection,
    updatePlanMetadata,
    clearPlan 
  } = usePlanningStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isCanvasMode, setIsCanvasMode] = useState(false);
  const [mobileView, setMobileView] = useState<'library' | 'canvas' | 'summary'>('library');
  const [isLibraryOpen, setIsLibraryOpen] = useState(true);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);

  // Filter apps based on search and category
  const filteredApps = apps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(apps.map(app => app.category)))];

  const handleAppDrop = useCallback((app: App, zoneId: string) => {
    if (readonly) return;
    
    addAppToPlan({
      app,
      position: { x: 100, y: 100 },
      connectionIds: []
    });
  }, [addAppToPlan, readonly]);

  const handleConnectionCreate = useCallback((fromAppId: string, toAppId: string) => {
    if (readonly) return;
    
    addConnection({
      id: `${fromAppId}-${toAppId}`,
      fromAppId,
      toAppId,
      type: 'trigger-action',
      config: {}
    });
  }, [addConnection, readonly]);

  const handleSavePlan = useCallback(() => {
    if (onSave && currentPlan) {
      onSave(currentPlan);
    }
  }, [onSave, currentPlan]);

  // Mobile navigation tabs
  const MobileNav = () => (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50">
      <div className="grid grid-cols-3 h-16">
        <button
          onClick={() => setMobileView('library')}
          className={`flex flex-col items-center justify-center space-y-1 ${
            mobileView === 'library' 
              ? 'text-blue-600 dark:text-blue-400' 
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <CubeIcon className="w-5 h-5" />
          <span className="text-xs">Apps</span>
        </button>
        <button
          onClick={() => setMobileView('canvas')}
          className={`flex flex-col items-center justify-center space-y-1 ${
            mobileView === 'canvas' 
              ? 'text-blue-600 dark:text-blue-400' 
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <Squares2X2Icon className="w-5 h-5" />
          <span className="text-xs">Canvas</span>
        </button>
        <button
          onClick={() => setMobileView('summary')}
          className={`flex flex-col items-center justify-center space-y-1 relative ${
            mobileView === 'summary' 
              ? 'text-blue-600 dark:text-blue-400' 
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <ListBulletIcon className="w-5 h-5" />
          <span className="text-xs">Plan</span>
          {currentPlan && currentPlan.apps.length > 0 && (
            <span className="absolute -top-1 right-4 h-2 w-2 bg-blue-600 rounded-full"></span>
          )}
        </button>
      </div>
    </div>
  );

  // App Library Component
  const AppLibrary = () => (
    <div className={`
      ${mobileView === 'library' ? 'block' : 'hidden'} lg:block
      w-full lg:w-80 bg-white dark:bg-gray-800 
      ${isLibraryOpen ? '' : 'lg:w-12'}
      lg:border-r border-gray-200 dark:border-gray-700 flex flex-col
      transition-all duration-300
    `}>
      <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${isLibraryOpen ? '' : 'lg:hidden'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            App Library
          </h2>
          <button
            onClick={() => setIsLibraryOpen(!isLibraryOpen)}
            className="hidden lg:block p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeftIcon className={`w-5 h-5 ${isLibraryOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>
        
        {/* Search */}
        <input
          type="text"
          placeholder="Search apps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        
        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
      </div>
      
      {/* Toggle button when sidebar is closed */}
      {!isLibraryOpen && (
        <button
          onClick={() => setIsLibraryOpen(true)}
          className="hidden lg:flex items-center justify-center h-12 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      )}
      
      {/* App List */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${isLibraryOpen ? '' : 'lg:hidden'} mb-16 lg:mb-0`}>
        {isLoading ? (
          <div className="text-center text-gray-500 dark:text-gray-400">
            Loading apps...
          </div>
        ) : (
          filteredApps.map(app => (
            <AppCard
              key={app.id}
              app={app}
              onClick={(app) => {
                if (!readonly) {
                  handleAppDrop(app, 'canvas');
                  // On mobile, switch to canvas view after adding
                  if (window.innerWidth < 1024) {
                    setMobileView('canvas');
                  }
                }
              }}
            />
          ))
        )}
      </div>
    </div>
  );

  // Planning Canvas Component
  const PlanningCanvas = () => (
    <div className={`
      ${mobileView === 'canvas' ? 'block' : 'hidden'} lg:block
      flex-1 flex flex-col
    `}>
      {/* Toolbar */}
      <PlanningToolbar
        currentPlan={currentPlan}
        onSave={handleSavePlan}
        onLoad={onLoad}
        onClear={clearPlan}
        onToggleMode={() => setIsCanvasMode(!isCanvasMode)}
        isCanvasMode={isCanvasMode}
        readonly={readonly}
      />

      {/* Planning Canvas */}
      <div className="flex-1 relative mb-16 lg:mb-0">
        {isCanvasMode ? (
          <ConnectionCanvas
            plan={currentPlan}
            onAppMove={(appId, position) => {
              // Update app position in store
            }}
            onConnectionCreate={handleConnectionCreate}
            onConnectionDelete={removeConnection}
            readonly={readonly}
          />
        ) : (
          <DroppableZone
            id="planning-area"
            onDrop={handleAppDrop}
            className="h-full"
          >
            <div className="h-full p-4 sm:p-6">
              {currentPlan?.apps.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <PlusIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-base sm:text-lg font-medium mb-2">Start Building Your Connection</p>
                    <p className="text-xs sm:text-sm">
                      {window.innerWidth < 1024 
                        ? 'Tap the Apps tab to add apps'
                        : 'Click on apps from the sidebar to add them'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentPlan.apps.map((planApp) => (
                    <div
                      key={planApp.app.id}
                      className="relative group"
                    >
                      <AppCard
                        app={planApp.app}
                        onClick={() => {}}
                      />
                      {!readonly && (
                        <button
                          onClick={() => removeAppFromPlan(planApp.app.id)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DroppableZone>
        )}
      </div>
    </div>
  );

  // Summary Sidebar Component
  const SummarySidebar = () => (
    <div className={`
      ${mobileView === 'summary' ? 'block' : 'hidden'} lg:block
      ${currentPlan && currentPlan.apps.length > 0 ? '' : 'lg:hidden'}
      w-full lg:w-80 bg-white dark:bg-gray-800 
      ${isSummaryOpen ? '' : 'lg:w-12'}
      lg:border-l border-gray-200 dark:border-gray-700 flex flex-col
      transition-all duration-300
    `}>
      <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${isSummaryOpen ? '' : 'lg:hidden'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Connection Plan
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {currentPlan?.apps.length || 0} apps • {currentPlan?.connections.length || 0} connections
            </p>
          </div>
          <button
            onClick={() => setIsSummaryOpen(!isSummaryOpen)}
            className="hidden lg:block p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronRightIcon className={`w-5 h-5 ${isSummaryOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </div>
      
      {/* Toggle button when sidebar is closed */}
      {!isSummaryOpen && (
        <button
          onClick={() => setIsSummaryOpen(true)}
          className="hidden lg:flex items-center justify-center h-12 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      )}
      
      <div className={`flex-1 overflow-y-auto p-4 ${isSummaryOpen ? '' : 'lg:hidden'} mb-16 lg:mb-0`}>
        {currentPlan && (
          <>
            {/* Plan Metadata */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Plan Name
              </label>
              <input
                type="text"
                value={currentPlan.name}
                onChange={(e) => updatePlanMetadata({ name: e.target.value })}
                placeholder="My Connection Plan"
                disabled={readonly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={currentPlan.description || ''}
                onChange={(e) => updatePlanMetadata({ description: e.target.value })}
                placeholder="Describe what this connection does..."
                rows={3}
                disabled={readonly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              />
            </div>

            {/* Apps in Plan */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Connected Apps
              </h4>
              <div className="space-y-2">
                {currentPlan.apps.map((planApp) => (
                  <div
                    key={planApp.app.id}
                    className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700"
                  >
                    {planApp.app.icon_url ? (
                      <img
                        src={planApp.app.icon_url}
                        alt={planApp.app.name}
                        className="h-6 w-6 rounded"
                      />
                    ) : (
                      <div className="h-6 w-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {planApp.app.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <span className="text-sm text-gray-900 dark:text-white flex-1">
                      {planApp.app.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Connections */}
            {currentPlan.connections.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Connections
                </h4>
                <div className="space-y-2">
                  {currentPlan.connections.map((connection) => {
                    const fromApp = currentPlan.apps.find(a => a.app.id === connection.fromAppId)?.app;
                    const toApp = currentPlan.apps.find(a => a.app.id === connection.toAppId)?.app;
                    
                    return (
                      <div
                        key={connection.id}
                        className="flex items-center space-x-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-xs"
                      >
                        <span className="text-gray-900 dark:text-white">{fromApp?.name}</span>
                        <span className="text-gray-500">→</span>
                        <span className="text-gray-900 dark:text-white">{toApp?.name}</span>
                        {!readonly && (
                          <button
                            onClick={() => removeConnection(connection.id)}
                            className="ml-auto text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <DndProvider backend={isTouchDevice ? TouchBackend : HTML5Backend}>
      <div className="flex h-full bg-gray-50 dark:bg-gray-900">
        {/* Desktop Layout */}
        <div className="hidden lg:flex w-full">
          <AppLibrary />
          <PlanningCanvas />
          <SummarySidebar />
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden w-full">
          <AppLibrary />
          <PlanningCanvas />
          <SummarySidebar />
        </div>

        {/* Mobile Navigation */}
        <MobileNav />
      </div>
    </DndProvider>
  );
}